import { useState } from 'react';
import {
  AppButton,
  AppChip,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppScreenScaffold,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
  CaseTypeTile,
  Icon,
  IconButton,
  type IconName,
} from '@core/designsystem';
import type { CaseLink, CaseStatus, CaseType, EventType } from '@core/domain/model';
import { NewEventDialog } from '@core/ui/NewEventDialog';
import { ClientPicker } from './ClientPicker';
import { typeLabel } from './formatter';
import type { CaseDetailState, EventKind } from './useCaseDetail';
import './cases.css';

export interface CaseDetailActions {
  setTitle: (title: string) => Promise<void>;
  setDescription: (value: string) => Promise<void>;
  setNotes: (value: string) => Promise<void>;
  setStatus: (status: CaseStatus) => Promise<void>;
  setType: (type: CaseType, typeOther: string | null) => Promise<void>;
  setClient: (clientId: string | null) => Promise<void>;
  createEvent: (draft: { title: string; type: EventType; startAt: number }) => Promise<void>;
  createClient: (draft: {
    fullName: string;
    phone: string | null;
    email: string | null;
  }) => Promise<void>;
  setLinks: (links: CaseLink[]) => Promise<void>;
  togglePinned: () => Promise<void>;
  addTag: (name: string) => Promise<void>;
  removeTag: (tagId: string) => Promise<void>;
  remove: () => Promise<void>;
  restore: () => Promise<void>;
}

/**
 * Открывает ссылку. Адрес без схемы («sudrf.ru») браузер иначе считает относительным
 * и уводит на несуществующую страницу приложения — та же поправка, что на Android.
 */
const openLink = (url: string) =>
  window.open(url.includes('://') ? url : `https://${url}`, '_blank', 'noopener');

const STATUSES: CaseStatus[] = ['Active', 'Suspended', 'Completed'];
const TYPES: CaseType[] = ['CourtCase', 'ClaimWork', 'Consultation', 'Enforcement', 'Other'];

const EVENT_ICON: Record<EventKind, IconName> = {
  hearing: 'gavel',
  meeting: 'group',
  task: 'checklist',
  overdue: 'error',
};

/**
 * Карточка дела — главный экран приложения.
 *
 * Собрана из блоков ([D-36](docs/DECISIONS.md)): заголовок над блоком, единый левый край,
 * одна иконка 24 на строку. Приглашение «Добавить …» набрано цветом действия и с иконкой —
 * иначе заполненное описание не отличить от пустого приглашения его добавить.
 */
export function CaseDetailScreen({
  state,
  actions,
  onBack,
  onOpenDocuments,
  onOpenDocument,
  onOpenClient,
  onOpenEvent,
}: {
  state: CaseDetailState;
  actions: CaseDetailActions;
  onBack: () => void;
  onOpenDocuments: () => void;
  /** Строка превью ведёт в сам документ, а не в общий список: он в ней и назван. */
  onOpenDocument: (id: string) => void;
  onOpenClient: (id: string) => void;
  onOpenEvent: (id: string) => void;
}) {
  const [editing, setEditing] = useState<
    'title' | 'description' | 'notes' | 'tag' | 'typeOther' | null
  >(null);
  const [picking, setPicking] = useState<'status' | 'type' | 'client' | null>(null);
  const [linkDraft, setLinkDraft] = useState<{ link: CaseLink; index: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  if (state.notFound) {
    return (
      <AppScreenScaffold topBar={<AppTopBar title="Дело" onBack={onBack} />}>
        <AppEmptyState icon="error" text="Дело не найдено: возможно, оно удалено" />
      </AppScreenScaffold>
    );
  }

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title={state.title}
          subtitle={state.typeLabel}
          onBack={onBack}
          actions={<IconButton icon="more_vert" label="Ещё" onClick={() => setMenuOpen(true)} />}
        />
      }
      overlay={
        <>
          {menuOpen ? (
            <AppMenuSheet
              title={state.title}
              onDismiss={() => setMenuOpen(false)}
              items={[
                {
                  icon: 'edit',
                  label: 'Переименовать',
                  onSelect: () => setEditing('title'),
                },
                {
                  icon: 'keep',
                  label: state.isPinned ? 'Открепить' : 'Закрепить',
                  onSelect: () => void actions.togglePinned(),
                },
                {
                  icon: 'delete',
                  label: 'Удалить дело',
                  danger: true,
                  onSelect: () => setConfirmDelete(true),
                },
              ]}
            />
          ) : null}

          {editing === 'typeOther' ? (
            <TextDialog
              title="Название типа"
              value={state.typeOther ?? ''}
              onDismiss={() => setEditing(null)}
              onSave={(value: string) => {
                void actions.setType('Other', value.trim() || null);
                setEditing(null);
              }}
            />
          ) : null}

          {editing === 'title' ? (
            <TextDialog
              title="Название дела"
              value={state.title}
              onDismiss={() => setEditing(null)}
              onSave={(value) => {
                if (value.trim()) void actions.setTitle(value.trim());
                setEditing(null);
              }}
            />
          ) : null}

          {editing === 'description' ? (
            <TextDialog
              title="Описание"
              value={state.description ?? ''}
              multiline
              onDismiss={() => setEditing(null)}
              onSave={(value) => {
                void actions.setDescription(value);
                setEditing(null);
              }}
            />
          ) : null}

          {editing === 'notes' ? (
            <TextDialog
              title="Заметки"
              value={state.notes ?? ''}
              multiline
              onDismiss={() => setEditing(null)}
              onSave={(value) => {
                void actions.setNotes(value);
                setEditing(null);
              }}
            />
          ) : null}

          {editing === 'tag' ? (
            <TagDialog
              known={state.knownTags.map((tag) => tag.name)}
              onDismiss={() => setEditing(null)}
              onAdd={(name) => {
                if (name.trim()) void actions.addTag(name.trim());
                setEditing(null);
              }}
            />
          ) : null}

          {picking === 'status' ? (
            <AppMenuSheet
              title="Состояние дела"
              onDismiss={() => setPicking(null)}
              items={STATUSES.map((status) => ({
                icon: status === state.status ? 'check' : 'folder',
                label: { Active: 'Активное', Suspended: 'Приостановлено', Completed: 'Завершено' }[
                  status
                ],
                onSelect: () => void actions.setStatus(status),
              }))}
            />
          ) : null}

          {picking === 'type' ? (
            <AppMenuSheet
              title="Тип дела"
              onDismiss={() => setPicking(null)}
              items={TYPES.map((type) => ({
                icon: type === state.type ? 'check' : 'folder',
                label: typeLabel({ type, typeOther: null }),
                onSelect: () => {
                  // У «Другого» спрашиваем своё название — как при создании дела:
                  // «Другое» в списке из десяти дел юристу ничего не говорит.
                  if (type === 'Other') setEditing('typeOther');
                  else void actions.setType(type, null);
                },
              }))}
            />
          ) : null}

          {/*
            Тот же выбор клиента, что и при создании дела: с поиском, с «Новым клиентом»
            и с «Убрать клиента». Списком без поиска это было до 09.09.2026 — у юриста
            с сотней клиентов такой список бесполезен, а Android искал здесь с самого
            начала. «Убрать» названо словом: юрист, не нашедший, как отвязать клиента
            от дела, шёл в его карточку и удалял его из записной книжки целиком.
          */}
          {picking === 'client' ? (
            <ClientPicker
              clients={state.knownClients}
              clearLabel="Убрать клиента"
              onDismiss={() => setPicking(null)}
              onSelect={(id) => {
                void actions.setClient(id);
                setPicking(null);
              }}
              onCreate={(draft) => {
                void actions.createClient(draft);
                setPicking(null);
              }}
            />
          ) : null}

          {linkDraft ? (
            <LinkDialog
              draft={linkDraft.link}
              isNew={linkDraft.index < 0}
              onDismiss={() => setLinkDraft(null)}
              onSave={(link) => {
                const links =
                  linkDraft.index < 0
                    ? [...state.links, link]
                    : state.links.map((old, at) => (at === linkDraft.index ? link : old));

                void actions.setLinks(links);
                setLinkDraft(null);
              }}
              onDelete={() => {
                void actions.setLinks(state.links.filter((_, at) => at !== linkDraft.index));
                setLinkDraft(null);
              }}
            />
          ) : null}

          {addingEvent ? (
            <NewEventDialog
              date={Date.now()}
              cases={[]}
              fixedCaseId={state.id}
              onDismiss={() => setAddingEvent(false)}
              onCreate={(draft) => {
                void actions.createEvent(draft);
                setAddingEvent(false);
              }}
            />
          ) : null}

          {confirmDelete ? (
            <AppFormDialog
              title="Удалить дело?"
              onDismiss={() => setConfirmDelete(false)}
              confirm={{
                label: 'Удалить',
                onClick: () => {
                  void actions.remove();
                  setConfirmDelete(false);
                },
              }}
            >
              {/* Единственное необратимое действие в приложении: оно уносит документы,
                  события и файлы дела. Поэтому здесь диалог, а не «Отменить» ([D-12]). */}
              <p className="case-detail__warning">
                Вместе с делом скроются его документы и события. Отменить можно
                в течение нескольких секунд.
              </p>
            </AppFormDialog>
          ) : null}

          {state.isDeleted ? (
            <AppUndoSnackbar
              text="Дело удалено"
              onUndo={() => void actions.restore()}
              onExpire={onBack}
            />
          ) : null}
        </>
      }
    >
      {/* Пока дело читается, показывать нечего: строки с пустыми значениями
          юрист прочтёт как «в деле ничего нет». */}
      <div className="case-detail" hidden={state.isLoading}>
        <AppSectionCard title="Состояние">
          <AppSectionRow
            icon="folder"
            title={state.statusLabel}
            subtitle={state.typeLabel}
            onClick={() => setPicking('status')}
            trailing={<CaseTypeTile type={state.typeAccent} />}
          />
          <AppSectionDivider />
          <AppSectionRow
            icon="label"
            title="Тип дела"
            subtitle={state.typeLabel}
            onClick={() => setPicking('type')}
          />
        </AppSectionCard>

        <AppSectionCard title="О деле">
          {state.description ? (
            <AppSectionRow
              icon="description"
              title={state.description}
              onClick={() => setEditing('description')}
            />
          ) : (
            <AppSectionRow
              icon="add"
              title="Добавить описание"
              action
              onClick={() => setEditing('description')}
            />
          )}
          <AppSectionDivider />

          {/*
            Теги — чипами, и снимается любой, а не последний. Строкой «нажмите, чтобы
            снять последний» средний тег из трёх убрать было нечем: приходилось снимать
            и заводить заново те, что мешали.
          */}
          {state.tags.length > 0 ? (
            <div className="case-detail__tags">
              <Icon name="label" size={24} className="section-row__icon" />
              <div className="case-detail__chips">
                {state.tags.map((tag) => (
                  <AppChip
                    key={tag.id}
                    label={tag.name}
                    selected
                    onClick={() => void actions.removeTag(tag.id)}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <AppSectionRow
            icon="add"
            title="Добавить тег"
            action
            onClick={() => setEditing('tag')}
          />
          <AppSectionDivider />

          {state.notes ? (
            <AppSectionRow
              icon="edit"
              title={state.notes}
              onClick={() => setEditing('notes')}
            />
          ) : (
            <AppSectionRow
              icon="add"
              title="Добавить заметку"
              action
              onClick={() => setEditing('notes')}
            />
          )}
        </AppSectionCard>

        <AppSectionCard title="Клиент">
          {state.client ? (
            <AppSectionRow
              icon="person"
              title={state.client.name}
              subtitle={state.client.phone ?? undefined}
              onClick={() => onOpenClient(state.client!.id)}
              trailing={
                <IconButton icon="edit" label="Сменить клиента" onClick={() => setPicking('client')} />
              }
            />
          ) : (
            <AppSectionRow
              icon="person"
              title="Выбрать клиента"
              action
              onClick={() => setPicking('client')}
            />
          )}
        </AppSectionCard>

        <AppSectionCard
          title="Документы"
          action={
            state.documentsTotal > 0
              ? { label: `Все (${state.documentsTotal})`, onClick: onOpenDocuments }
              : undefined
          }
        >
          {state.documents.map((document, index) => (
            <div key={document.id}>
              {index > 0 ? <AppSectionDivider /> : null}
              <AppSectionRow
                icon="picture_as_pdf"
                title={document.title}
                subtitle={document.meta}
                onClick={() => onOpenDocument(document.id)}
                trailing={document.isFavorite ? <FavoriteMark /> : undefined}
              />
            </div>
          ))}
          {state.documents.length > 0 ? <AppSectionDivider /> : null}
          <AppSectionRow
            icon="document_scanner"
            title="Сканировать документ"
            action
            onClick={onOpenDocuments}
          />
        </AppSectionCard>

        <AppSectionCard title="Задачи и события">
          {state.events.map((event, index) => (
            <div key={event.id}>
              {index > 0 ? <AppSectionDivider /> : null}
              <AppSectionRow
                icon={EVENT_ICON[event.kind]}
                title={event.title}
                subtitle={event.timing}
                onClick={() => onOpenEvent(event.id)}
              />
            </div>
          ))}
          {state.events.length > 0 ? <AppSectionDivider /> : null}
          {/* Заседание назначают, глядя в дело, а не открывая календарь: дело здесь
              уже известно, и спрашивать о нём нечего. */}
          <AppSectionRow
            icon="add"
            title="Добавить событие"
            action
            onClick={() => setAddingEvent(true)}
          />
        </AppSectionCard>

        <AppSectionCard title="Ссылки">
          {state.links.map((link, index) => (
            <div key={`${link.url}-${index}`}>
              {index > 0 ? <AppSectionDivider /> : null}
              {/* Строка открывает ссылку, карандаш — правит её. Опечатку в адресе
                  иначе нечем исправить, а неверную ссылку — нечем убрать. */}
              <AppSectionRow
                icon="link"
                title={link.title || link.url}
                subtitle={link.title ? link.url : undefined}
                onClick={() => openLink(link.url)}
                trailing={
                  <IconButton
                    icon="edit"
                    label="Изменить ссылку"
                    onClick={() => setLinkDraft({ link, index })}
                  />
                }
              />
            </div>
          ))}
          {state.links.length > 0 ? <AppSectionDivider /> : null}
          <AppSectionRow
            icon="add"
            title="Добавить ссылку"
            action
            onClick={() => setLinkDraft({ link: { title: '', url: '' }, index: -1 })}
          />
        </AppSectionCard>
      </div>
    </AppScreenScaffold>
  );
}

const FavoriteMark = () => <span className="case-detail__favorite">★</span>;

/** Правка одного текстового поля: название, описание, заметка. */
function TextDialog({
  title,
  value,
  multiline = false,
  onDismiss,
  onSave,
}: {
  title: string;
  value: string;
  multiline?: boolean;
  onDismiss: () => void;
  onSave: (value: string) => void;
}) {
  const [text, setText] = useState(value);

  return (
    <AppFormDialog
      title={title}
      onDismiss={onDismiss}
      confirm={{ label: 'Сохранить', onClick: () => onSave(text) }}
    >
      <AppTextField
        value={text}
        onChange={setText}
        label={title}
        multiline={multiline}
        autoFocus
        selectAllOnFocus={!multiline}
      />
    </AppFormDialog>
  );
}

/**
 * Тег заводится по имени: юристу не нужно знать, существовал он уже или нет.
 * Заведённые раньше показываются рядом — набирать заново «банкротство» бессмысленно.
 */
function TagDialog({
  known,
  onDismiss,
  onAdd,
}: {
  known: string[];
  onDismiss: () => void;
  onAdd: (name: string) => void;
}) {
  const [name, setName] = useState('');

  return (
    <AppFormDialog
      title="Тег дела"
      onDismiss={onDismiss}
      confirm={{ label: 'Добавить', onClick: () => onAdd(name) }}
    >
      <AppTextField value={name} onChange={setName} label="Название тега" autoFocus />

      {known.length > 0 ? (
        <div className="case-detail__known-tags">
          {known.map((tag) => (
            <AppButton key={tag} variant="text" onClick={() => onAdd(tag)}>
              {tag}
            </AppButton>
          ))}
        </div>
      ) : null}
    </AppFormDialog>
  );
}

/**
 * Внешняя ссылка дела — «Паспорт» §6.2: карточка на сайте суда, папка в облаке.
 * У ссылки есть название, потому что адрес карточки дела нечитаем, а узнать её
 * юристу нужно с одного взгляда.
 */
/**
 * Ссылка дела: заводится и правится одним диалогом.
 *
 * У заведённой ссылки левая кнопка удаляет её — другого места для этого нет,
 * а третья кнопка в ряд на телефоне не помещается. Так же сделано на Android
 * (`CaseLinkDialog.kt`).
 */
function LinkDialog({
  draft,
  isNew,
  onDismiss,
  onSave,
  onDelete,
}: {
  draft: CaseLink;
  isNew: boolean;
  onDismiss: () => void;
  onSave: (link: CaseLink) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(draft.title);
  const [url, setUrl] = useState(draft.url);

  return (
    <AppFormDialog
      title={isNew ? 'Новая ссылка' : 'Ссылка'}
      onDismiss={onDismiss}
      dismissLabel={isNew ? 'Отмена' : 'Удалить'}
      onDismissClick={isNew ? undefined : onDelete}
      confirm={{
        label: isNew ? 'Добавить' : 'Готово',
        onClick: () => {
          const address = url.trim();
          if (address) onSave({ title: title.trim(), url: address });
        },
      }}
    >
      <AppTextField value={title} onChange={setTitle} label="Название" autoFocus />
      <AppTextField value={url} onChange={setUrl} label="Адрес" />
    </AppFormDialog>
  );
}
